import React from "react";
import GatewayPdf from "../GatewayPdf";
import { useGatewayExpand, useHeaderTask, usePrintGateway } from "./GatewaySummary.hooks";
import {
  getGatewayContainerClasses,
  getGatewayHeaderClasses,
  gatewayClass,
  shouldShowHeaderMilestone,
} from "./GatewaySummary.utils";
import { GatewayExpandControl } from "./components/GatewayExpandControl";
import { GatewayHeader } from "./components/GatewayHeader";
import { TaskSummaryList } from "./components/TaskSummaryList";
import { HeaderTaskMilestone } from "./components/HeaderTaskMilestone";
import { GatewaySummaryProps } from "./GatewaySummary.types";

/**
 * GatewaySummary component displays a gateway with its progress, tasks, and interactive controls
 * Manages expand/collapse, printing, and NA (Not Applicable) functionality
 *
 * This is the main refactored component with extracted hooks and sub-components
 * Reduced from 843 LOC to ~150 LOC
 */
export const GatewaySummary: React.FC<GatewaySummaryProps> = ({
  gateway,
  gatewayData,
  setGatewayData,
  backgroundReloadGatewayDataToggle,
  setBackgroundReloadGatewayDataToggle,
  projectID,
  gatewayIndex,
  description,
  children,
  toBeExpanded,
  tab,
}) => {
  // Manage expand/collapse state
  const { expand, setExpand, handleExpand } = useGatewayExpand(gateway, toBeExpanded);

  // Find and track header task within gateway
  const { headerGateway, headerGatewayIndex } = useHeaderTask(gateway);

  // Manage print functionality
  const { printContent, componentRef, handlePrint } = usePrintGateway(
    projectID,
    gateway.gatewaysID,
    gateway.gatewayTemplateID
  );

  return (
    <div className={getGatewayContainerClasses(gateway)}>
      {/* PDF Container for printing */}
      <div className="PDFContainer">
        <GatewayPdf ref={componentRef} gateway={printContent} />
      </div>

      {/* Gateway Header Section */}
      <div className={getGatewayHeaderClasses(gateway, tab)} onClick={handleExpand}>
        <div
          className={`Row ${!gateway.description ? "NoDescription" : ""} ${headerGateway ? "HeaderGateway" : ""}`}
        >
          {/* Gateway Progress Indicator */}
          <div
            className={`GatewayProgress ${gatewayClass(
              gateway.completedTasksCount,
              gateway.tasksCount,
              gateway.bGatewayNAEnabled && gateway.bGatewayNAValue
            )}`}
          >
            <div className="Count">
              {`${gateway.bGatewayNAEnabled && gateway.bGatewayNAValue ? "N/A" : gateway.completedTasksCount + "/" + gateway.tasksCount}`}
            </div>
          </div>

          {/* Gateway Title, Description, and Header Content */}
          <div
            className={`GatewayTitle ${gateway.description ? "" : "NameOnly"} ${headerGateway ? "HeaderGateway" : ""}`}
          >
            <GatewayHeader
              gateway={gateway}
              expand={expand}
              onExpand={handleExpand}
              onPrint={handlePrint}
              headerGateway={headerGateway}
              tab={tab}
            />
          </div>

          {/* Header Task Milestone (shown in Gateways tab) */}
          {shouldShowHeaderMilestone(tab, headerGateway, gateway) &&
            headerGateway &&
            typeof headerGatewayIndex === "number" &&
            typeof gatewayIndex === "number" &&
            gatewayData &&
            setGatewayData &&
            typeof backgroundReloadGatewayDataToggle === "boolean" &&
            setBackgroundReloadGatewayDataToggle && (
              <div className="HeaderGatewayContainer">
                <HeaderTaskMilestone
                  task={headerGateway}
                  bEditable={gateway.bEditable}
                  projectID={projectID}
                  gatewaysID={gateway.gatewaysID}
                  gatewayTemplateID={gateway.gatewayTemplateID}
                  gatewayIndex={gatewayIndex}
                  taskIndex={headerGatewayIndex}
                  gatewayData={gatewayData}
                  setGatewayData={setGatewayData}
                  backgroundReloadGatewayDataToggle={backgroundReloadGatewayDataToggle}
                  setBackgroundReloadGatewayDataToggle={setBackgroundReloadGatewayDataToggle}
                />
              </div>
            )}
        </div>

        {/* Expand Control & NA Checkbox */}
        <GatewayExpandControl
          gateway={gateway}
          expand={expand}
          onExpand={handleExpand}
          setExpand={setExpand}
          onPrint={handlePrint}
          projectID={projectID}
          gatewayIndex={gatewayIndex}
          gatewayData={gatewayData}
          setGatewayData={setGatewayData}
          backgroundReloadGatewayDataToggle={backgroundReloadGatewayDataToggle}
          setBackgroundReloadGatewayDataToggle={setBackgroundReloadGatewayDataToggle}
        />
      </div>

      {/* Task List */}
      <TaskSummaryList gateway={gateway} expand={expand} headerGatewayIndex={headerGatewayIndex}>
        {children}
      </TaskSummaryList>
    </div>
  );
};
